import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  topicReducer,
  createEmptyState,
  type TopicReducerTopic,
  type TopicState,
} from './reducer';
import { TopicLogic } from './logic';
import { apiCall } from '@/util/api';
import type { Topic } from './types';

interface TopicStateContextType {
  state: TopicState;
  dispatch: React.Dispatch<TopicReducerTopic>;
  // Re-fetches the topic list. Callers use this when visibility may have
  // changed (e.g. after the user verifies a phone number and becomes an
  // invited user), so by-invitation-only topics appear/disappear in the list.
  refetchTopics: () => Promise<void>;
}

const TopicStateContext = createContext<TopicStateContextType | undefined>(
  undefined
);

interface TopicStateProviderProps {
  children: React.ReactNode;
}

export const TopicStateProvider: React.FC<TopicStateProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(
    topicReducer,
    undefined,
    createEmptyState
  );

  const hasFetchedRef = useRef(false);

  const fetchTopics = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', loading: true });
    try {
      const response = await apiCall(
        '/api/topics?thumbnailImageOnly=true&skipCustomTemplates=true'
      );
      if (response.ok) {
        const data = await response.json();
        const topics = data.map((a: Topic) => TopicLogic.fromJSON(a));
        dispatch({ type: 'SET_TOPICS', topics });
      }
    } catch {
      // silent — list stays empty, per-page errors are handled elsewhere
    } finally {
      dispatch({ type: 'SET_LOADING', loading: false });
    }
  }, []);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchTopics();
  }, [fetchTopics]);

  const value: TopicStateContextType = useMemo(
    () => ({ state, dispatch, refetchTopics: fetchTopics }),
    [state, fetchTopics]
  );

  return (
    <TopicStateContext.Provider value={value}>
      {children}
    </TopicStateContext.Provider>
  );
};

export const useTopicState = (): TopicStateContextType => {
  const context = useContext(TopicStateContext);
  if (context === undefined) {
    throw new Error('useTopicState must be used within an TopicStateProvider');
  }
  return context;
};
